// =============================================
// Optimization Service
// v3.0: 2-pass optimization with Hybrid Data Protection
// =============================================

import { getLLMProvider, getStageConfig } from "@/services/llm";
import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import { logDebug } from "./contentLogger";
import { protectLinks, restoreLinks, withLinkMarkerNote } from "./linkWeaver";

// =============================================
// Hybrid Data Protection
// =============================================

interface ProtectionMap {
  placeholders: Map<string, string>; // [[SRC_001]] → full citation
  spans: Map<string, string>;        // NUM_a1b2 → "20%"
}

const SOURCE_RE = /\((?:Source|Źródło|Quelle):\s*(?:[^()]*|\([^()]*\))*\)/g;
const NUM_RE = /\b\d[\d,.\-–]*\s*(?:%|procent|mln|mld|tys|kg|g|mg|μg|m|cm|km|l|ml|ha|kcal|cal|kWh|Hz|MHz|GHz|zł|PLN|USD|EUR|\$|€)\b/g;
const DATE_RE = /\b(?:19|20)\d{2}(?:\s*[-–]\s*(?:19|20)\d{2})?\b/g;
const DOI_RE = /\b10\.\d{4,}\/[^\s]+/g;

function generateId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Apply Hybrid Data Protection: replace sources with placeholders, wrap numbers in spans.
 */
function protectData(html: string): { protectedHtml: string; map: ProtectionMap } {
  const map: ProtectionMap = {
    placeholders: new Map(),
    spans: new Map(),
  };

  let result = html;

  // Step 1: Replace source citations with placeholders
  result = result.replace(SOURCE_RE, (match) => {
    const id = `[[SRC_${String(map.placeholders.size + 1).padStart(3, "0")}]]`;
    map.placeholders.set(id, match);
    return id;
  });

  // Step 2: Wrap numbers/dates in spans (visible to LLM but tracked)
  result = result.replace(NUM_RE, (match) => {
    const id = generateId("NUM");
    map.spans.set(id, match);
    return `<span data-token-id="${id}">${match}</span>`;
  });

  result = result.replace(DATE_RE, (match) => {
    // Don't wrap if already inside a span
    const id = generateId("DAT");
    map.spans.set(id, match);
    return `<span data-token-id="${id}">${match}</span>`;
  });

  result = result.replace(DOI_RE, (match) => {
    const id = generateId("DOI");
    map.spans.set(id, match);
    return `<span data-token-id="${id}">${match}</span>`;
  });

  return { protectedHtml: result, map };
}

/**
 * Restore protected data: replace placeholders and unwrap spans.
 */
function restoreData(html: string, map: ProtectionMap): string {
  let result = html;

  // Step 1: Unwrap spans (keep content, remove tags)
  result = result.replace(/<span data-token-id="[^"]*">([\s\S]*?)<\/span>/g, "$1");

  // Step 2: Restore placeholders
  for (const [placeholder, original] of map.placeholders) {
    result = result.replaceAll(placeholder, original);
  }

  return result;
}

/**
 * Validate that no data was lost during LLM processing.
 */
function validateProtection(
  inputHtml: string,
  outputHtml: string,
  map: ProtectionMap,
): { valid: boolean; warnings: string[] } {
  const warnings: string[] = [];

  // Check placeholders are all restored
  for (const [placeholder] of map.placeholders) {
    if (outputHtml.includes(placeholder)) {
      warnings.push(`Unreplaced placeholder: ${placeholder}`);
    }
  }

  // Check number values are preserved
  for (const [, value] of map.spans) {
    if (!outputHtml.includes(value)) {
      warnings.push(`Missing data value: ${value}`);
    }
  }

  // Check source count preserved
  const inputSources = (inputHtml.match(/\((?:Source|Źródło|Quelle):/g) || []).length;
  const outputSources = (outputHtml.match(/\((?:Source|Źródło|Quelle):/g) || []).length;
  if (outputSources < inputSources) {
    warnings.push(`Sources reduced: ${inputSources} → ${outputSources}`);
  }

  return { valid: warnings.length === 0, warnings };
}

// =============================================
// Optimization passes
// =============================================

/**
 * PASS 1: Article Check — copywriting rules A, C, D, E, F, I
 */
async function articleCheckPass(
  html: string,
  language: string,
  targetLength: number,
  dbPrompt: { template: string; model: string; temperature?: number | null } | null,
  providerInstance: ReturnType<typeof getLLMProvider>,
  model: string,
  mapId?: string,
  nodeId?: string,
): Promise<{ result: string; costUsd: number }> {
  const maxLength = Math.round(targetLength * 1.2);

  const templateVars: Record<string, string> = {
    language,
    max_length: String(maxLength),
    target_length: String(targetLength),
    article_html: html,
  };

  const prompt = dbPrompt
    ? fillTemplate(dbPrompt.template, templateVars)
    : `Jesteś ekspertem copywritingu SEO. Zastosuj poniższe reguły do artykułu. Język: ${language}.

## Reguły do zastosowania

**A. Zero First Person** — Usuń "polecam", "uważam", "my sugerujemy", "I recommend". Zamień na formę bezosobową.
**C. One Definition per Term** — Definicja tylko przy pierwszym użyciu terminu. Usuń powtórzenia.
**D. Parenthetical Cleanup** — Nawiasy max 5 słów, max 1 na akapit. Dłuższe → osobne zdanie lub usuń.
**E. Tone Down Bold Claims** — Zamień superlatywy: "rewolucyjny"→"skuteczny", "gwarantowany"→"oczekiwany", "najlepszy na świecie"→"jeden z najlepszych".
**F. Reduce Imperatives** — Max 2-3 tryby rozkazujące per sekcja H2. "Sprawdź"→"Można sprawdzić", "Twoja strona"→"strona".
**I. Simplify Technical Descriptions** — Dla kroków technicznych: co robi (1 zdanie) + kto powinien to zrobić.

## Ograniczenia
- MAX ${maxLength} słów (docelowo ~${targetLength})
- NIE modyfikuj danych w <span data-token-id="..."> ani [[SRC_*]] placeholderów
- Zachowaj WSZYSTKIE nagłówki H1, H2, H3
- Zwróć TYLKO HTML, bez komentarzy

## Artykuł do optymalizacji
${html}`;

  const response = await providerInstance.call(withLinkMarkerNote(prompt, html), {
    model,

    temperature: dbPrompt?.temperature ?? 0.3,
    step: "optimization", mapId, nodeId,
  });

  return { result: response.text.trim(), costUsd: response.costUsd || 0 };
}

/**
 * PASS 2: Intermediate — flow, transitions, formatting (G, H, K)
 */
async function intermediatePass(
  html: string,
  language: string,
  providerInstance: ReturnType<typeof getLLMProvider>,
  model: string,
  mapId?: string,
  nodeId?: string,
): Promise<{ result: string; costUsd: number }> {
  const inputLength = html.split(/\s+/).length;
  const maxGrowth = Math.round(inputLength * 1.1);

  // Load intermediate prompt from DB (optimization_intermediate)
  const dbPromptIntermediate = await loadPrompt("optimization_intermediate");

  const templateVarsIntermediate: Record<string, string> = {
    language,
    article_html: html,
    max_growth: String(maxGrowth),
  };

  const prompt = dbPromptIntermediate
    ? fillTemplate(dbPromptIntermediate.template, templateVarsIntermediate)
    : `Jestes ekspertem od flow i formatowania tresci. Zastosuj reguly. Jezyk: ${language}.

## Reguly

**G. Info Hierarchy + Breathing Room** — Co 2-3 geste akapity → lzejszy akapit przejsciowy.
**H. Natural Transitions** — 1-2 wstawki per H2: mini-podsumowania, przyznanie trudnosci, kontekstualizacja.
**K1. Bold (<strong>)** — Kluczowe terminy przy pierwszym uzyciu + zaskakujace liczby. 2-4 per H2.
**K2. Italic (<i>)** — Wazne stwierdzenia + obcojezyczne terminy. 1-2 per H2.
**K3. Blockquote (<blockquote>)** — Cytaty lub kluczowe definicje. 0-2 per artykul.
**K4. Line breaks (<br />) ** — 5-15 per artykul.

## Ograniczenia
- MAX ${maxGrowth} slow (+10% od input)
- NIE modyfikuj danych w <span data-token-id="..."> ani [[SRC_*]]
- NIE dodawaj NOWYCH external linkow. ZACHOWAJ istniejace internal linki (<a href="/...">) dokladnie jak sa
- Zachowaj WSZYSTKIE H1, H2, H3
- Zwroc TYLKO HTML

## Artykul
${html}`;

  // Use intermediate stage config if DB prompt overrides provider
  const intStageConfig = getStageConfig("optimization_intermediate");
  const intProvider = dbPromptIntermediate
    ? getLLMProvider(dbPromptIntermediate.provider || intStageConfig.provider)
    : providerInstance;
  const intModel = dbPromptIntermediate?.modelId || model;

  const response = await intProvider.call(withLinkMarkerNote(prompt, html), {
    model: intModel,

    temperature: dbPromptIntermediate?.temperature ?? 0.4,
    step: "optimization_intermediate", mapId, nodeId,
  });

  return { result: response.text.trim(), costUsd: response.costUsd || 0 };
}

// =============================================
// Voice protection marker strip
// =============================================

/**
 * pass1Html appends a `<!-- VOICE_PROTECTION -->` separator + voice rules so
 * the LLM reads guidance on what NOT to flatten. Empirically (33% of ready
 * nodes pre-fix), pass1 LLM trims the rules text after the marker but echoes
 * the marker itself back into its output, then pass2 propagates it, and it
 * lands in final_html. Strip on each LLM boundary as defense-in-depth.
 *
 * Case-insensitive marker text + flexible whitespace inside the comment to
 * survive LLM normalizations like `<!--VOICE_PROTECTION-->` or `<!-- voice_protection  -->`.
 */
const VOICE_MARKER_RE = /\s*<!--\s*VOICE_PROTECTION\s*-->[\s\S]*$/i;
function stripVoiceMarker(html: string): string {
  return html.replace(VOICE_MARKER_RE, "");
}

// =============================================
// Main optimization function
// =============================================

export interface OptimizationResult {
  optimizedHtml: string;
  costUsd: number;
  warnings: string[];
  report: {
    inputWords: number;
    outputWords: number;
    growthPercent: number;
    sourcesPreserved: boolean;
  };
}

/**
 * Run 2-pass optimization with Hybrid Data Protection.
 */
export async function optimizeArticle(
  enrichedHtml: string,
  language: string,
  targetWordCount = 1500,
  writingProfile?: { preferred_phrases?: string[]; banned_phrases?: string[]; voice_pronouns?: string } | null,
  mapId?: string,
  nodeId?: string,
): Promise<OptimizationResult> {
  let totalCost = 0;
  const inputWords = enrichedHtml.split(/\s+/).length;

  // Load prompt from DB (user-editable in Settings → Prompty AI)
  const dbPrompt = await loadPrompt("optimization");
  const stageConfig = getStageConfig("optimization");
  const optProvider = getLLMProvider(dbPrompt?.provider || stageConfig.provider);
  const optModel = dbPrompt?.modelId || stageConfig.model;

  // 1. Apply Hybrid Data Protection
  // OSLONA LINKOW [[LNK_nnn]] (2026-08-23): LLM historycznie usuwal <a> —
  // tokenizacja PRZED protectData (zeby NUM/DATE nie owijaly nic wewnatrz <a>).
  const { html: linkProtectedHtml, map: linkMap } = protectLinks(enrichedHtml);
  const { protectedHtml, map } = protectData(linkProtectedHtml);

  // 1b. Inject author voice protection if profile exists
  let voiceProtection = "";
  if (writingProfile) {
    const ppList = writingProfile.preferred_phrases || [];
    const bpList = writingProfile.banned_phrases || [];
    const pp = ppList.join(", ");
    const bp = bpList.join(", ");
    const voice = writingProfile.voice_pronouns || "standard";
    voiceProtection = `\n\n### AUTHOR VOICE PROTECTION\nWriting profile:\n- Preferred phrases (do NOT remove without reason): ${pp}\n- Banned phrases (REMOVE if found): ${bp}\n- Voice: ${voice}\n\nRULES:\n- Do NOT remove author's preferred phrases unless they violate other rules\n- Do NOT change voice pronouns if profile says a specific voice\n- Do NOT flatten style to generic neutral tone\n- Do NOT cut more than 25% of content without explicit reason`;
    // Log informatywny: pokaz co faktycznie zostalo injected per komponent.
    // Stary log "(empty)" sprawdzal tylko preferred_phrases — myslacy gdy bp+voice byly OK.
    logDebug(
      "OPT",
      `Voice protection injected: preferred=${ppList.length}, banned=${bpList.length}, voice=${voice}` +
      (ppList.length === 0 && bpList.length === 0 && voice === "standard"
        ? " ⚠ WSZYSTKIE komponenty puste — profile prawdopodobnie nie wypelniony"
        : "")
    );
  } else {
    logDebug("OPT", "Voice protection skipped: brak writing profile w ctx (active_profile_id=null lub fetch fail)");
  }

  // 2. PASS 1: Article Check
  const pass1Html = voiceProtection ? protectedHtml + `\n<!-- VOICE_PROTECTION -->${voiceProtection}` : protectedHtml;
  const pass1 = await articleCheckPass(pass1Html, language, targetWordCount, dbPrompt, optProvider, optModel, mapId, nodeId);
  totalCost += pass1.costUsd;
  const pass1Stripped = stripVoiceMarker(pass1.result);

  // 3. PASS 2: Intermediate
  const pass2 = await intermediatePass(pass1Stripped, language, optProvider, optModel, mapId, nodeId);
  totalCost += pass2.costUsd;
  const pass2Stripped = stripVoiceMarker(pass2.result);

  // 4. Restore protected data (final guard: re-strip in case pass2 LLM re-emitted the marker after restoreData)
  const restoredNoLinks = stripVoiceMarker(restoreData(pass2Stripped, map));
  const linkRestore = restoreLinks(restoredNoLinks, linkMap);
  const restoredHtml = linkRestore.html;

  // 5. Validate
  const validation = validateProtection(enrichedHtml, restoredHtml, map);
  if (linkRestore.warnings.length > 0) {
    validation.warnings.push(...linkRestore.warnings);
    validation.valid = false;
  }

  const outputWords = restoredHtml.split(/\s+/).length;

  return {
    optimizedHtml: restoredHtml,
    costUsd: totalCost,
    warnings: validation.warnings,
    report: {
      inputWords,
      outputWords,
      growthPercent: Math.round(((outputWords - inputWords) / inputWords) * 100),
      sourcesPreserved: validation.valid,
    },
  };
}
